import { SpDropdownToggle } from 'components/shared/Dropdown';
import { ChangeEvent, FC, KeyboardEvent, MouseEventHandler, useCallback, useState } from 'react';
import { Dropdown, DropdownDivider, DropdownItem, DropdownMenu } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import type { GroupColor } from 'state/quotes/dashboard/tiles/tiles.model';
import styled from 'styled-components';

import { ColorSelector } from '../colorSelector/ColorSelector';

interface GroupMenuProps {
  groupId: string;
  className?: string;
  selectedColor?: GroupColor;
  onMenuToggled: (visible: boolean) => void;
  onDeleted: (groupId: string) => void;
  onRenamed?: (previousGroupId: string, newGroupId: string) => void;
  onColorSelected?: (groupId: string, color?: GroupColor) => void;
}
export const GroupMenu: FC<GroupMenuProps> = ({
  groupId,
  selectedColor,
  className,
  onMenuToggled,
  onDeleted,
  onRenamed,
  onColorSelected,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen(prevState => !prevState);

  const onDeleteClicked = useCallback(() => onDeleted(groupId), [groupId, onDeleted]);

  const onGroupNameChanged = useCallback(
    (newGroupName: string) => onRenamed && onRenamed(groupId, newGroupName),
    [groupId, onRenamed],
  );

  const _onColorSelected = useCallback(
    (color?: GroupColor) => onColorSelected && onColorSelected(groupId, color),
    [groupId, onColorSelected],
  );

  return (
    <Dropdown
      show={dropdownOpen}
      onToggle={() => {
        toggle();
        onMenuToggled(!dropdownOpen);
      }}
      className={className}
    >
      <SpDropdownToggle
        variant="link"
        caret={false}
        className="btn btn-flat-secondary btn-icon px-0"
        data-e2e={`filter-${groupId}-menu`}
        data-tour="quoteDashboard.deleteGroup"
      >
        <em className="icon">more_vert</em>
      </SpDropdownToggle>
      <DropdownMenu>
        {onRenamed && (
          <>
            <RenameGroupDropdownItem
              initialGroupName={groupId}
              onGroupRenamed={onGroupNameChanged}
              e2e={`filter-${groupId}-menu-rename-group`}
              key={`rename-${dropdownOpen}`}
            />
            <DropdownDivider />
          </>
        )}
        <DeleteGroupDropdownItem
          key={`delete-${dropdownOpen}`}
          onDeleted={() => {
            onDeleteClicked();
            setDropdownOpen(false);
          }}
          e2e={`filter-${groupId}-menu-delete`}
        />
        {onColorSelected && (
          <>
            <DropdownDivider />
            <DropdownItem>
              <ColorSelector selectedColor={selectedColor} onColorSelected={_onColorSelected} />
            </DropdownItem>
          </>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

GroupMenu.displayName = 'GroupMenu';

const CustomGroupContainer = styled.div`
  min-width: 15rem !important;
`;

interface RenameGroupDropdownItemProps {
  e2e: string;
  initialGroupName: string;
  onGroupRenamed(groupName: string): void;
}

const RenameGroupDropdownItem: FC<RenameGroupDropdownItemProps> = ({ e2e, initialGroupName, onGroupRenamed }) => {
  const { formatMessage } = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [editingGroupName, setEditingGroupName] = useState(initialGroupName);

  const onAskEditing: MouseEventHandler<HTMLElement> = event => {
    event.stopPropagation();
    setIsEditing(true);
  };

  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setEditingGroupName(e.target.value);
  }, []);

  const onValidate = useCallback(() => {
    const sanitizedGroupName = editingGroupName.trim();
    if (!!sanitizedGroupName && sanitizedGroupName !== initialGroupName) {
      onGroupRenamed(sanitizedGroupName);
    }
  }, [editingGroupName, initialGroupName, onGroupRenamed]);

  const onInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'Enter':
        case 'Tab':
          onValidate();
          break;
        case 'Escape':
          {
            setIsEditing(false);
            setEditingGroupName('');
          }
          break;
      }
    },
    [onValidate],
  );

  return (
    <DropdownItem onClick={onAskEditing} data-e2e={e2e}>
      {!isEditing && <span>{formatMessage({ id: 'dashboard.renameGroup' })}</span>}
      {isEditing && (
        <CustomGroupContainer className="form-group m-0">
          <div className="input-group">
            <input
              type="text"
              autoFocus
              className="form-control"
              value={editingGroupName}
              onChange={onChange}
              onKeyDown={onInputKeyDown}
              data-e2e={`${e2e}-input`}
            />
            <button className="btn btn-primary" type="button" onClick={onValidate} data-e2e={`${e2e}-validate-btn`}>
              {formatMessage({ id: 'ok' })}
            </button>
          </div>
        </CustomGroupContainer>
      )}
    </DropdownItem>
  );
};

RenameGroupDropdownItem.displayName = 'RenameGroupDropdownItem';

interface DeleteGroupDropdownItemProps {
  e2e: string;
  onDeleted: () => void;
}

const DeleteGroupDropdownItem: FC<DeleteGroupDropdownItemProps> = ({ e2e, onDeleted }) => {
  const { formatMessage } = useIntl();
  const [isAskingConfirmation, setIsAskingConfirmation] = useState(false);

  const onAskConfirmationToggled: MouseEventHandler<HTMLElement> = event => {
    event.stopPropagation();
    setIsAskingConfirmation(!isAskingConfirmation);
  };

  return (
    <DropdownItem as="div" onClick={onAskConfirmationToggled} data-e2e={e2e}>
      {!isAskingConfirmation && <span className="text-danger">{formatMessage({ id: 'delete' })}</span>}
      {isAskingConfirmation && (
        <div className="d-flex justify-content-between">
          <button
            className="btn btn-lg btn-discreet-secondary flex-grow-1 px-1 mx-1 flex-basis-100"
            data-e2e={`${e2e}-cancel`}
          >
            {formatMessage({ id: 'cancel' })}
          </button>
          <button
            className="btn btn-lg btn-outline-danger flex-grow-1 px-1  mx-1 flex-basis-100"
            onClick={onDeleted}
            data-e2e={`${e2e}-confirm`}
          >
            {formatMessage({ id: 'dashboard.closeAndDelete' })}
          </button>
        </div>
      )}
    </DropdownItem>
  );
};

RenameGroupDropdownItem.displayName = 'RenameGroupDropdownItem';
